var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useMemo } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useSelector } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import { useTheme } from '@pinwheel/origami';
import safelinkImage from '../../assets/safelink.svg';
import { useGetLocalizedString } from '../../hooks/useGetLocalizedString';
import { TemplateContinue } from '../../components/TemplateContinue';
import { selectFeatureFlags, selectIsAppClip, selectLinkTokenPayload, } from '../../state/slices/slice-link-params';
import { getDeviceType } from '../../utils/device-detection';
import usePageTracking from '../../hooks/usePageTracking';
import { EventSurface } from '../../types/events';
import TermsOfService, { TermsOfServiceTextVariant, } from '../../TermsOfService';
import { DeviceType } from '../../types/device-types';
import { selectLatestAppClipEvent } from '../../state/slices/slice-link-api';
import useLinkNavigate from '../../hooks/useLinkNavigate';
import { pollAppClipEvents } from '../../utils/poll-app-clip-events';
import { useOpts } from '../../context';
import useFireClientEvent from '../../hooks/useFireClientEvent';
var APP_CLIP_WEBPAGE_BASE_URL = 'https://cdn.getpinwheel.com/pinwheel-experience.html';
var SafeLinkImage = function () { return React.createElement("img", { src: safelinkImage, alt: "" }); };
var QRCodeImage = function (_a) {
    var appClipUrl = _a.appClipUrl;
    var resolveColor = useTheme().resolveColor;
    var defaultColor = resolveColor({
        type: 'theme',
        colorLegacy: 'Blue/50',
        colorNext: 'brand/default',
    });
    return React.createElement(QRCodeSVG, { value: appClipUrl, fgColor: defaultColor, size: 240 });
};
// TODO: add UI test
function TemplateAppClip(_a) {
    var appClipInitializationToken = _a.appClipInitializationToken, skipAppClip = _a.skipAppClip;
    var getLocalizedString = useGetLocalizedString();
    var navigate = useLinkNavigate();
    var exit = useOpts().exit;
    var featureFlags = useSelector(selectFeatureFlags);
    var deviceType = getDeviceType();
    var titleText = useMemo(function () {
        var _a;
        var titleTextMap = (_a = {},
            _a[DeviceType.IOS] = getLocalizedString('SAFELINK_TITLE'),
            _a[DeviceType.ANDROID] = getLocalizedString('SAFELINK_TITLE'),
            _a[DeviceType.WEB] = getLocalizedString('SAFELINK_TITLE_WEB'),
            _a);
        return titleTextMap[deviceType];
    }, [deviceType, getLocalizedString]);
    var subtitleText = useMemo(function () {
        var _a;
        var subtitleTextMap = (_a = {},
            _a[DeviceType.IOS] = getLocalizedString('SAFELINK_SUBTITLE_IOS'),
            _a[DeviceType.ANDROID] = getLocalizedString('SAFELINK_SUBTITLE_ANDROID'),
            _a[DeviceType.WEB] = getLocalizedString('SAFELINK_SUBTITLE_WEB'),
            _a);
        return subtitleTextMap[deviceType];
    }, [deviceType, getLocalizedString]);
    var buttonLabel = useMemo(function () { return getLocalizedString('CONTINUE'); }, [getLocalizedString]);
    var appClipUrl = useMemo(function () {
        // Create a new URL with the app clip initialization token set as a query
        // param `t`.
        var url = new URL(APP_CLIP_WEBPAGE_BASE_URL);
        url.searchParams.append('t', appClipInitializationToken);
        return url.toString();
    }, [appClipInitializationToken]);
    var isAppClip = useSelector(selectIsAppClip);
    var fireClientEvent = useFireClientEvent();
    var fireImpressionEvent = usePageTracking(EventSurface.APP_CLIP_PRIMER).fireImpressionEvent;
    var linkTokenPayload = useSelector(selectLinkTokenPayload);
    useEffect(function () {
        var stopPolling = pollAppClipEvents(fireClientEvent, featureFlags.forwardAppClipEvents);
        return function () {
            stopPolling();
        };
    }, [fireClientEvent, featureFlags]);
    var latestEvent = useSelector(selectLatestAppClipEvent);
    useEffect(function () {
        if ((latestEvent === null || latestEvent === void 0 ? void 0 : latestEvent.event.name) === 'exit') {
            exit();
        }
        else if ((latestEvent === null || latestEvent === void 0 ? void 0 : latestEvent.event.name) === 'success') {
            if (featureFlags.skipSuccessScreen ||
                featureFlags.skipSuccessOrErrorTerminalScreen) {
                exit();
            }
            else {
                navigate('/success');
            }
        }
    }, [exit, latestEvent, navigate, featureFlags]);
    useEffect(function () {
        if (isAppClip) {
            // If we ever see this template from within the App Clip, then there's
            // either a corner case (e.g. app is outdated) or there's a bug in our
            // code. In either case, fallback to the default flow so that we don't
            // enter an infinitely recursive flow of showing the app clip prompt.
            datadogLogs.logger.error('app_clip app_clip_template_on_device_2', {
                device_type: deviceType,
            });
            skipAppClip();
            return;
        }
        // Temporary FF gates to allow us to revert the Android and web rollouts if
        // needed during the initial implementation/release phases.
        if ((deviceType === DeviceType.ANDROID &&
            !featureFlags.appClipsAndroidEnabled) ||
            (deviceType === DeviceType.WEB && !featureFlags.appClipsWebEnabled)) {
            datadogLogs.logger.info('app_clip app_clip_template_skip', {
                device_type: deviceType,
            });
            skipAppClip();
            return;
        }
        // Only fire an impression if we're not immediately firing the `app_clip_skip` action.
        fireImpressionEvent({});
        datadogLogs.logger.info('app_clip app_clip_template_not_skipped', {
            device_type: deviceType,
        });
    }, [
        deviceType,
        featureFlags.appClipsAndroidEnabled,
        featureFlags.appClipsWebEnabled,
        fireImpressionEvent,
        isAppClip,
        skipAppClip,
    ]);
    var imageComponent = deviceType === DeviceType.WEB ? (React.createElement(QRCodeImage, { appClipUrl: appClipUrl })) : (React.createElement(SafeLinkImage, null));
    return (React.createElement(TemplateContinue, __assign({ Image: function () { return imageComponent; }, title: titleText, subtitle: subtitleText, subtitleVariant: "caption", buttonLabel: buttonLabel, buttonEventId: "TemplateAppClip-continue", onContinue: function () {
            datadogLogs.logger.info('app_clip app_clip_template_continue_click', {
                device_type: deviceType,
            });
            window.open(appClipUrl);
        } }, (deviceType === DeviceType.WEB && {
        // Don't show the continue button on web
        actions: [],
    }), { extraControls: linkTokenPayload.skipIntroScreen ? (React.createElement(TermsOfService, __assign({}, (deviceType === DeviceType.WEB && {
            // Override the default text (by `selecting "Continue" ...`) since
            // it doesn't make sense in the context of scanning a QR code.
            textVariant: TermsOfServiceTextVariant.PROCEEDING,
        })))) : null })));
}
export default TemplateAppClip;
